import { useRef } from 'react';
import { ApiError } from './api';
import { WhatToIncludeLink } from './WhatToIncludeLink';

const MAX_NOTES = 160;
const MAX_PHOTOS = 10;

export interface StructureNotesPhotosFieldsProps {
  notes: string;
  onNotesChange: (next: string) => void;
  files: File[];
  onFilesChange: (next: File[]) => void;
  disabled?: boolean;
}

/**
 * Shared "Additional Requirements or Notes" + "Additional Photos" inputs.
 * Used inline in the dimensions wizard pages so the step matches the legacy
 * single-page configuration flow instead of forcing a separate notes page.
 */
export function StructureNotesPhotosFields({
  notes,
  onNotesChange,
  files,
  onFilesChange,
  disabled,
}: StructureNotesPhotosFieldsProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  return (
    <>
      <div className="mb-2">
        <label className="form-label fs-5" htmlFor="structure_notes">
          Additional Requirements or Notes
        </label>
        <WhatToIncludeLink />
        <textarea
          id="structure_notes"
          className="form-control"
          rows={3}
          maxLength={MAX_NOTES}
          value={notes}
          onChange={(e) => onNotesChange(e.target.value.slice(0, MAX_NOTES))}
          placeholder="OPTIONAL: Add any additional information we should know (i.e. special requirements, project details, etc). 160 character maximum."
          disabled={disabled}
        />
        <small className="form-text text-muted float-end">
          {notes.length} / {MAX_NOTES}
        </small>
      </div>

      <div className="mb-2">
        <label className="form-label fs-5" htmlFor="photos">
          Additional Photos{' '}
          <small className="text-muted">(JPEG/PNG, up to {MAX_PHOTOS})</small>
        </label>
        <WhatToIncludeLink />
        <input
          ref={inputRef}
          type="file"
          id="photos"
          className="form-control form-control-sm"
          accept="image/jpeg,image/png"
          multiple
          disabled={disabled}
          onChange={(e) =>
            onFilesChange(Array.from(e.target.files ?? []).slice(0, MAX_PHOTOS))
          }
        />
        {files.length > 0 && (
          <ul className="list-unstyled small text-muted mt-2">
            {files.map((f, i) => (
              <li key={i}>
                • {f.name} ({Math.round(f.size / 1024)} KB)
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

/** Uploads the supplied photos to a structure; no-op when files is empty. */
export async function uploadStructurePhotos(structureId: string | number, files: File[]): Promise<void> {
  if (files.length === 0) return;
  const fd = new FormData();
  for (const f of files) fd.append('photos', f);
  const res = await fetch(`/api/v1/structures/${structureId}/photos`, {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  if (!res.ok) {
    const body = await res.text();
    throw new ApiError(res.status, body, body || `Photo upload failed (${res.status})`);
  }
}

export const STRUCTURE_NOTES_MAX = MAX_NOTES;
export const STRUCTURE_PHOTOS_MAX = MAX_PHOTOS;
